
import { useState } from 'react'
import { Calendar, FileText, Loader2 } from 'lucide-react'
import { format, addDays } from 'date-fns'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Calendar as CalendarComponent } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'
import { useDietPlansV2, useCreateCalendarAssignment } from '@/api/diet-plans-v2'
import type { DietPlanV2 } from '@/types/diet-v2'

interface AssignDietPlanV2ModalProps {
  userId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onAssigned?: () => void
}

const DURATION_DAYS: Record<string, number> = {
  day: 0,
  week: 6,
  month: 29,
}

const DURATION_LABEL: Record<string, string> = {
  day: '1 day',
  week: '7 days',
  month: '30 days',
}

export function AssignDietPlanV2Modal({
  userId,
  open,
  onOpenChange,
  onAssigned,
}: AssignDietPlanV2ModalProps) {
  const [selectedPlan, setSelectedPlan] = useState<DietPlanV2 | null>(null)
  const [planSearch, setPlanSearch] = useState('')
  const [planPickerOpen, setPlanPickerOpen] = useState(false)
  const [startDate, setStartDate] = useState<Date | undefined>()
  const [notes, setNotes] = useState('')

  const { data: plansData, isLoading: plansLoading } = useDietPlansV2({ limit: 100 })
  const plans = plansData?.data ?? []
  const assign = useCreateCalendarAssignment()

  const endDate =
    selectedPlan && startDate
      ? addDays(startDate, DURATION_DAYS[selectedPlan.duration] ?? 0)
      : null

  const filteredPlans =
    planSearch.trim() === ''
      ? plans
      : plans.filter((p) =>
          p.name.toLowerCase().includes(planSearch.toLowerCase())
        )

  const handleAssign = async () => {
    if (!selectedPlan || !startDate) return
    try {
      await assign.mutateAsync({
        userId,
        dietPlanId: selectedPlan._id,
        startDate: format(startDate, 'yyyy-MM-dd'),
        notes: notes.trim() || undefined,
      } as any)
      onOpenChange(false)
      onAssigned?.()
      // Reset
      setSelectedPlan(null)
      setStartDate(undefined)
      setNotes('')
    } catch {
      // Error handled by mutation
    }
  }

  const handleOpenChange = (v: boolean) => {
    if (!v) {
      setSelectedPlan(null)
      setPlanSearch('')
      setStartDate(undefined)
      setNotes('')
    }
    onOpenChange(v)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Assign Diet Plan</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-1">
          {/* Plan picker */}
          <div className="space-y-2">
            <Label>Diet Plan *</Label>
            <Popover open={planPickerOpen} onOpenChange={setPlanPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !selectedPlan && 'text-muted-foreground'
                  )}
                >
                  <FileText className="mr-2 h-4 w-4 shrink-0" />
                  {selectedPlan ? selectedPlan.name : 'Select a diet plan...'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[480px] p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder="Search plans..."
                    value={planSearch}
                    onValueChange={setPlanSearch}
                  />
                  <CommandList>
                    {plansLoading ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        Loading plans...
                      </div>
                    ) : (
                      <>
                        <CommandEmpty>No plans found.</CommandEmpty>
                        <CommandGroup>
                          {filteredPlans.map((plan) => (
                            <CommandItem
                              key={plan._id}
                              value={plan._id}
                              onSelect={() => {
                                setSelectedPlan(plan)
                                setPlanPickerOpen(false)
                                setPlanSearch('')
                              }}
                            >
                              <div className="flex items-center justify-between w-full gap-3">
                                <div className="min-w-0">
                                  <p className="font-medium truncate">{plan.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {Math.round(plan.totalCalories)} kcal/day ·{' '}
                                    P {Math.round(plan.macros.protein)}g · C {Math.round(plan.macros.carbs)}g · F {Math.round(plan.macros.fat)}g
                                  </p>
                                </div>
                                <div className="flex gap-1.5 shrink-0">
                                  <Badge variant="outline" className="text-xs capitalize">
                                    {plan.type.replace('-', ' ')}
                                  </Badge>
                                  <Badge variant="secondary" className="text-xs">
                                    {DURATION_LABEL[plan.duration] ?? plan.duration}
                                  </Badge>
                                </div>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Plan summary */}
          {selectedPlan && (
            <div className="rounded-lg border bg-muted/40 px-4 py-3 space-y-1 text-sm">
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                <span>
                  <span className="text-muted-foreground">Duration: </span>
                  <strong>{DURATION_LABEL[selectedPlan.duration] ?? selectedPlan.duration}</strong>
                </span>
                <span>
                  <span className="text-muted-foreground">Calories: </span>
                  <strong>{Math.round(selectedPlan.totalCalories)} kcal/day</strong>
                </span>
                <span>
                  <span className="text-muted-foreground">Macros: </span>
                  <strong>
                    P {Math.round(selectedPlan.macros.protein)}g · C {Math.round(selectedPlan.macros.carbs)}g · F {Math.round(selectedPlan.macros.fat)}g
                  </strong>
                </span>
              </div>
            </div>
          )}

          {/* Start date */}
          <div className="space-y-2">
            <Label>Start Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !startDate && 'text-muted-foreground'
                  )}
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, 'PPP') : 'Pick a start date...'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <CalendarComponent
                  mode="single"
                  selected={startDate}
                  onSelect={setStartDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* End date preview */}
          {endDate && startDate && (
            <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5 text-sm">
              <span className="text-muted-foreground">Plan ends on: </span>
              <strong>{format(endDate, 'PPP')}</strong>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label>
              Notes{' '}
              <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Textarea
              placeholder="Any notes for this assignment..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selectedPlan || !startDate || assign.isPending}
          >
            {assign.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Assigning...
              </>
            ) : (
              'Assign Plan'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
